import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useConfigStore } from "@/stores/widget-store";
import { Button } from "../ui/button";
import { SettingGroup } from "../ui/setting-group";
import { SETTINGS_CONFIG, CATEGORIES, CATEGORY_LEARN_MORE } from "@/constants/settings-config";

interface SettingsTabProps {
  onSave: () => Promise<void>;
}

export const SettingsTab = ({ onSave }: SettingsTabProps) => {
  const { config, setFixes, isLoading, error } = useConfigStore();

  const getSettingValue = (key: string): boolean => {
    const keys = key.split(".");
    let value: unknown = config.fixes;

    for (const k of keys) {
      value = (value as Record<string, unknown>)?.[k];
    }

    return Boolean(value);
  };

  return (
    <Card className="cy:rounded-lg cy:gap-0 cy:shadow-none!">
        <CardHeader className="cy:border-b cy:border-gray-200 cy:pb-6">
          <CardTitle className="cy:text-xl">Issue fixes</CardTitle>
        <CardDescription>
        Enable these options on to have accessibility fixes applied automatically to your markup. No coding needed on your end.
        <br />
        <em>Note: This works only for content that's present when your page first loads. Elements added later through JavaScript won't be included.</em>
        </CardDescription>
        <CardAction></CardAction>
      </CardHeader>
      <CardContent className="cy:p-0">
        <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8">
          <div className="cy:flex cy:flex-col cy:gap-4 cy:py-6">
            {Object.values(CATEGORIES).map((category) => {
              const categorySettings = SETTINGS_CONFIG.filter(
                (setting) => setting.category === category
              );

              if (categorySettings.length === 0) return null;

              const learnMore = CATEGORY_LEARN_MORE[category as keyof typeof CATEGORY_LEARN_MORE];

              return (
                <SettingGroup
                  key={category}
                  category={category}
                  settings={categorySettings.map((setting) => ({
                    key: setting.key,
                    label: setting.label,
                    description: setting.description,
                    guideline: setting.guideline,
                    checked: getSettingValue(setting.key),
                    onCheckedChange: (checked) =>
                      setFixes(setting.key, checked),
                  }))}
                  learnMoreUrl={learnMore?.url}
                  learnMoreText={learnMore?.text}
                  learnMoreLinkText={learnMore?.linkText}
                />
              );
            })}
          </div>
          <div className="cy:py-2">
            {error && <div className="cy:text-red-500 cy:text-sm">{error}</div>}
            <Button
              onClick={onSave}
              disabled={isLoading}
              className="cy:rounded! cy:font-semibold!"
            >
              {isLoading ? "Saving..." : "Save settings"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
